import { useCallback, useEffect, useMemo, useState } from 'react'
import { handleUnauthorized } from '@/lib/api-error-handler'
import { BACKEND_URL } from '@/lib/config'
import { getAuthToken, useSupabaseAuth } from '@/lib/supabase'
import type { Connector, ConnectorType } from '@/lib/types'

export type UseConnectorsResult = {
  connectors: Connector[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  createConnector: (
    type: ConnectorType,
    name: string,
    config: Record<string, unknown>,
    credentials: Record<string, unknown>
  ) => Promise<Connector>
  updateConnector: (
    id: string,
    name?: string,
    config?: Record<string, unknown>,
    credentials?: Record<string, unknown>
  ) => Promise<Connector>
  deleteConnector: (id: string) => Promise<void>
  testConnector: (id: string) => Promise<{ success: boolean; error?: string }>
}

export function useConnectors(): UseConnectorsResult {
  const { isAuthenticated } = useSupabaseAuth()
  const [connectors, setConnectors] = useState<Connector[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    if (!isAuthenticated) {
      setConnectors([])
      return
    }

    const token = await getAuthToken()
    if (!token || !BACKEND_URL) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(`${BACKEND_URL}/api/connectors`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      })

      if (!response.ok) {
        if (response.status === 401) {
          await handleUnauthorized(response, 'Fetch connectors')
          return
        }
        throw new Error(`Failed to fetch connectors: ${response.status}`)
      }

      const data = await response.json()
      setConnectors(data.connectors || [])
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      console.warn('[useConnectors] Failed to fetch:', message)
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  const createConnector = useCallback(
    async (
      type: ConnectorType,
      name: string,
      config: Record<string, unknown>,
      credentials: Record<string, unknown>
    ): Promise<Connector> => {
      const token = await getAuthToken()
      if (!token) throw new Error('Not authenticated')
      if (!BACKEND_URL) throw new Error('Backend URL not configured')

      const response = await fetch(`${BACKEND_URL}/api/connectors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ type, name, config, credentials }),
      })

      if (!response.ok) {
        if (response.status === 401) {
          await handleUnauthorized(response, 'Create connector')
        }
        const data = await response.json().catch(() => ({}))
        throw new Error(
          data.detail ||
            data.error ||
            `Failed to create connector: ${response.status}`
        )
      }

      const data = await response.json()
      await refetch()
      return data.connector
    },
    [refetch]
  )

  const updateConnector = useCallback(
    async (
      id: string,
      name?: string,
      config?: Record<string, unknown>,
      credentials?: Record<string, unknown>
    ): Promise<Connector> => {
      const token = await getAuthToken()
      if (!token) throw new Error('Not authenticated')
      if (!BACKEND_URL) throw new Error('Backend URL not configured')

      const body: Record<string, unknown> = {}
      if (name !== undefined) body.name = name
      if (config !== undefined) body.config = config
      if (credentials !== undefined) body.credentials = credentials

      const response = await fetch(`${BACKEND_URL}/api/connectors/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        if (response.status === 401) {
          await handleUnauthorized(response, 'Update connector')
        }
        const data = await response.json().catch(() => ({}))
        throw new Error(
          data.detail ||
            data.error ||
            `Failed to update connector: ${response.status}`
        )
      }

      const data = await response.json()
      await refetch()
      return data
    },
    [refetch]
  )

  const deleteConnector = useCallback(
    async (id: string): Promise<void> => {
      const token = await getAuthToken()
      if (!token) throw new Error('Not authenticated')
      if (!BACKEND_URL) throw new Error('Backend URL not configured')

      const response = await fetch(`${BACKEND_URL}/api/connectors/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      })

      if (!response.ok) {
        if (response.status === 401) {
          await handleUnauthorized(response, 'Delete connector')
        }
        const data = await response.json().catch(() => ({}))
        throw new Error(
          data.detail ||
            data.error ||
            `Failed to delete connector: ${response.status}`
        )
      }

      await refetch()
    },
    [refetch]
  )

  const testConnector = useCallback(
    async (id: string): Promise<{ success: boolean; error?: string }> => {
      const token = await getAuthToken()
      if (!token) return { success: false, error: 'Not authenticated' }
      if (!BACKEND_URL)
        return { success: false, error: 'Backend URL not configured' }

      try {
        const response = await fetch(
          `${BACKEND_URL}/api/connectors/${id}/test`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'ngrok-skip-browser-warning': 'true',
            },
          }
        )

        const data = await response.json()

        if (!response.ok) {
          return {
            success: false,
            error: data.detail || data.error || 'Connection test failed',
          }
        }

        return {
          success: data.success ?? true,
          error: data.detail || data.error,
        }
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Connection test failed',
        }
      }
    },
    []
  )

  useEffect(() => {
    if (!isAuthenticated) {
      setConnectors([])
      return
    }

    void refetch()
  }, [isAuthenticated])

  return useMemo(
    () => ({
      connectors,
      isLoading,
      error,
      refetch,
      createConnector,
      updateConnector,
      deleteConnector,
      testConnector,
    }),
    [
      connectors,
      isLoading,
      error,
      refetch,
      createConnector,
      updateConnector,
      deleteConnector,
      testConnector,
    ]
  )
}
